import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { TrendingUp, TrendingDown, ShoppingBag, Layers, Euro } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const fmtEur = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n || 0));

/**
 * Trend pill — small badge with up/down arrow + % delta vs previous period.
 * Negative trends show in red, positive in emerald, null (no previous data) in gray.
 */
const TrendPill = ({ percent, invertColors = false, suffix = "%" }) => {
  if (percent === null || percent === undefined) {
    return (
      <span className="text-xs text-gray-400" data-testid="trend-pill-na">
        n/d
      </span>
    );
  }
  const isUp = percent >= 0;
  const isPositive = invertColors ? !isUp : isUp;
  const color = percent === 0
    ? "bg-gray-100 text-gray-600"
    : isPositive
    ? "bg-emerald-100 text-emerald-700"
    : "bg-red-100 text-red-700";
  const Icon = isUp ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}
      data-testid="trend-pill"
    >
      <Icon className="w-3 h-3" />
      {isUp ? "+" : ""}
      {percent}
      {suffix}
    </span>
  );
};

/**
 * Sparkline — minimal inline SVG of daily AOV across the current 30d window.
 * No external deps; renders nicely at any width thanks to viewBox.
 */
const Sparkline = ({ values }) => {
  if (!values || values.length < 2) {
    return (
      <div className="h-16 flex items-center justify-center text-xs text-gray-400">
        Non abbastanza giorni di dati
      </div>
    );
  }
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const w = 300;
  const h = 60;
  const step = w / (values.length - 1);
  const points = values
    .map((v, i) => `${i * step},${h - ((v - min) / span) * (h - 8) - 4}`)
    .join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-16" preserveAspectRatio="none" data-testid="aov-sparkline">
      <defs>
        <linearGradient id="aov-grad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline
        fill="url(#aov-grad)"
        stroke="none"
        points={`0,${h} ${points} ${w},${h}`}
      />
      <polyline
        fill="none"
        stroke="#059669"
        strokeWidth="1.8"
        strokeLinejoin="round"
        strokeLinecap="round"
        points={points}
      />
    </svg>
  );
};

/**
 * AOV Widget — compares Average Order Value of the last 30 days against the
 * previous 30 days. Helps measure the impact of cross-sell features
 * ("Continua la Collezione" + "Chi ha comprato questo, ha comprato anche…")
 * on basket size and multi-item order share.
 */
export const AOVWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const token = sessionStorage.getItem("access_token");
        const res = await axios.get(`${API}/dashboard/aov-comparison`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) setData(res.data);
      } catch (err) {
        console.warn("[aov-widget] fetch failed:", err?.message);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Card data-testid="aov-widget-loading">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Euro className="w-5 h-5 text-emerald-500" />
            AOV — Scontrino medio
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-24 animate-pulse bg-gray-100 rounded" />
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card data-testid="aov-widget-error">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Euro className="w-5 h-5 text-emerald-500" />
            AOV — Scontrino medio
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">Errore nel caricamento. Riprova più tardi.</p>
        </CardContent>
      </Card>
    );
  }

  const { current, previous, trends, daily, window_days } = data;
  const aovSeries = (daily || []).map((d) => d.aov);

  return (
    <Card data-testid="aov-widget">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 flex-wrap">
          <Euro className="w-5 h-5 text-emerald-500" />
          AOV — Scontrino medio
          <Badge variant="secondary" className="ml-2 font-normal">
            ultimi {window_days}gg vs {window_days}gg precedenti
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
          {/* AOV */}
          <div className="p-3 rounded-lg bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200" data-testid="aov-current-card">
            <div className="text-xs text-emerald-700 uppercase tracking-wide font-semibold mb-1">
              AOV attuale
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-2xl font-bold text-gray-900">{fmtEur(current.aov)}</span>
              <TrendPill percent={trends.aov_percent} />
            </div>
            <div className="text-xs text-gray-500 mt-1">
              precedente: {fmtEur(previous.aov)}
            </div>
          </div>

          {/* Revenue */}
          <div className="p-3 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200" data-testid="aov-revenue-card">
            <div className="text-xs text-blue-700 uppercase tracking-wide font-semibold mb-1 flex items-center gap-1">
              <ShoppingBag className="w-3.5 h-3.5" /> Fatturato
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-2xl font-bold text-gray-900">{fmtEur(current.revenue)}</span>
              <TrendPill percent={trends.revenue_percent} />
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {current.orders} ordini · prec. {previous.orders} (
              <TrendPill percent={trends.orders_percent} />)
            </div>
          </div>

          {/* Multi-item orders share (cross-sell impact) */}
          <div className="p-3 rounded-lg bg-gradient-to-br from-amber-50 to-amber-100/50 border border-amber-200" data-testid="aov-multiitem-card">
            <div className="text-xs text-amber-700 uppercase tracking-wide font-semibold mb-1 flex items-center gap-1">
              <Layers className="w-3.5 h-3.5" /> Ordini multi-articolo
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-2xl font-bold text-gray-900">{current.multi_item_share}%</span>
              <TrendPill percent={trends.multi_item_share_percent} />
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {current.multi_item_orders}/{current.orders} ordini · prec. {previous.multi_item_share}%
            </div>
          </div>
        </div>

        {/* Sparkline of daily AOV */}
        <div className="border-t pt-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              AOV giornaliero — ultimi 30gg
            </span>
            <span className="text-xs text-gray-400">{daily?.length || 0} giorni con ordini</span>
          </div>
          <Sparkline values={aovSeries} />
        </div>

        <p className="text-xs text-gray-500 mt-3">
          💡 L&apos;AOV cresce quando i clienti aggiungono più articoli per ordine. Le sezioni
          &laquo;Continua la Collezione&raquo; e &laquo;Chi ha comprato anche…&raquo; sono pensate
          per spingere questo numero verso l&apos;alto.
        </p>
      </CardContent>
    </Card>
  );
};
